import { useCallback, useEffect, useState } from 'react';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import PublishPanel from './ProjectEdit/PublishPanel';
import ParticipantsPanel from './ProjectEdit/ParticipantsPanel';
import AnnotationUIPanel from './ProjectEdit/AnnotationUIPanel';
import ItemsPanel from './ProjectEdit/ItemsPanel';
import RoiSettingsPanel from './ProjectEdit/RoiSettingsPanel';
import ItemMetadataSettingsPanel from './ProjectEdit/ItemMetadataSettingsPanel';
import NotesSettingsPanel from './ProjectEdit/NotesSettingsPanel';
import { getUISupportsProjectRoi } from './uiRegistry';
import { DRAFT_NAV_BLOCKED } from './projectSettingsDraft';
import styles from './ProjectEditForm.module.css';

function ProjectEditForm({
    lifecycleStatus,
    settingsLocked,
    onRequestPublish,
    onRequestReturnToDraft,
    onRequestClose,
    onRequestArchive,
    onRequestReopen,
    onRequestUnarchive,
    onRequestDeleteProject,
    participants,
    onApplyParticipantRole,
    annotationUI,
    setAnnotationUI,
    notesConfig,
    setNotesConfig,
    folderId,
    onItemsChanged,
    creatorId,
    hasUnsavedSettings,
    firstItemId,
    showRoiEditorLink,
    publishRoiChecking = false,
    publishPutPending = false,
    publishPutError = null,
    publishWarnings = [],
    publishBlocks = [],
    roiCoverageSuccessCount = null,
    publishRoiCheckError = null,
}) {
    const { user: currentUser } = useAuth();
    const [availableUIs, setAvailableUIs] = useState([]);
    const [projectItems, setProjectItems] = useState([]);
    const [projectSubfolders, setProjectSubfolders] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [itemsError, setItemsError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmDeleteError, setConfirmDeleteError] = useState(null);
    const [uisError, setUisError] = useState(null);
    const [participantError, setParticipantError] = useState(null);
    const [participantSaving, setParticipantSaving] = useState(false);

    useEffect(() => {
        setUisError(null);
        api.get('projects/uis', {})
            .then((r) => {
                if (r.status === 200 && Array.isArray(r.data)) setAvailableUIs(r.data);
                else setUisError(r.data?.message || 'Failed to load annotation UIs.');
            })
            .catch((e) => setUisError(e.message || 'Failed to load annotation UIs.'));
    }, []);

    const loadItems = useCallback(() => {
        if (!folderId) return;
        setItemsLoading(true);
        setItemsError(null);
        api.get(`projects/${folderId}/items`, {})
            .then((r) => {
                if (r.status === 200) {
                    const items = r.data.items || [];
                    setProjectItems(items);
                    setProjectSubfolders(r.data.subfolders || []);
                    if (onItemsChanged) onItemsChanged(items);
                } else {
                    setItemsError(r.data?.message || 'Failed to load items.');
                }
            })
            .catch((e) => setItemsError(e.message))
            .finally(() => setItemsLoading(false));
    }, [folderId, onItemsChanged]);

    useEffect(() => { loadItems(); }, [loadItems]);

    useEffect(() => {
        if (confirmDelete) setConfirmDeleteError(null);
    }, [confirmDelete]);

    function handleConfirmDelete() {
        if (!confirmDelete) return;
        setConfirmDeleteError(null);
        const { type, id } = confirmDelete;
        const req =
            type === 'item'
                ? api.delete(`projects/${folderId}/items/${id}`, {})
                : api.delete(`projects/folders/${id}`, {});
        req
            .then((r) => {
                if (r.status === 200) {
                    loadItems();
                    setConfirmDelete(null);
                } else {
                    setConfirmDeleteError(r.data?.message || 'Delete failed.');
                }
            })
            .catch((e) => setConfirmDeleteError(e.message || e.response?.data?.message || 'Delete failed.'));
    }

    const isSelfRemovalLocked = useCallback((userId, role) => {
        if (role !== 'admin') return false;
        if (!currentUser) return false;
        if (String(userId) !== String(currentUser._id)) return false;
        if (currentUser.admin) return false;
        if (creatorId && String(currentUser._id) === String(creatorId)) return false;
        return true;
    }, [currentUser, creatorId]);

    const runParticipantRoleOp = useCallback(async (payload) => {
        if (!onApplyParticipantRole) return false;
        setParticipantError(null);
        setParticipantSaving(true);
        try {
            const r = await onApplyParticipantRole(payload);
            if (r.status !== 200) {
                setParticipantError(r.data?.message || 'Failed to update participants.');
                return false;
            }
            return true;
        } catch (e) {
            setParticipantError(e.message || 'Failed to update participants.');
            return false;
        } finally {
            setParticipantSaving(false);
        }
    }, [onApplyParticipantRole]);

    const handleAddRole = useCallback(async (userId, role) => {
        const uid = String(userId);
        const existing = participants.find((x) => String(x.userId) === uid);
        if (existing?.roles?.includes(role)) return true;
        return runParticipantRoleOp({ op: 'add', userId, role });
    }, [participants, runParticipantRoleOp]);

    const handleRemoveRole = useCallback(async (userId, role) => {
        return runParticipantRoleOp({ op: 'remove', userId, role });
    }, [runParticipantRoleOp]);

    const projectChrome = {
        folderId,
        firstItemId,
        hasUnsavedSettings,
        showRoiEditorLink,
        draftNavBlockedTitle: DRAFT_NAV_BLOCKED,
    };

    const supportsProjectRoi = getUISupportsProjectRoi(annotationUI?.id);

    return (
        <div className={styles.form}>
            <section
                id="project-section-visibility"
                className={styles.settingsScrollAnchor}
                aria-label="Project status"
            >
                <PublishPanel
                    lifecycleStatus={lifecycleStatus}
                    hasUnsavedSettings={hasUnsavedSettings}
                    folderId={folderId}
                    annotationUI={annotationUI}
                    firstItemId={firstItemId}
                    onRequestPublish={onRequestPublish}
                    onRequestReturnToDraft={onRequestReturnToDraft}
                    onRequestClose={onRequestClose}
                    onRequestArchive={onRequestArchive}
                    onRequestReopen={onRequestReopen}
                    onRequestUnarchive={onRequestUnarchive}
                    onRequestDelete={onRequestDeleteProject}
                    publishRoiChecking={publishRoiChecking}
                    publishPutPending={publishPutPending}
                    publishPutError={publishPutError}
                    publishWarnings={publishWarnings}
                    publishBlocks={publishBlocks}
                    roiCoverageSuccessCount={roiCoverageSuccessCount}
                    publishRoiCheckError={publishRoiCheckError}
                />
            </section>

            <section
                id="project-section-participants"
                className={styles.settingsScrollAnchor}
                aria-label="Participants"
            >
                <ParticipantsPanel
                    participants={participants}
                    onAddRole={handleAddRole}
                    onRemoveRole={handleRemoveRole}
                    isSelfRemovalLocked={isSelfRemovalLocked}
                    participantError={participantError}
                    participantSaving={participantSaving}
                    folderId={folderId}
                    disableJoinCodes={participantSaving}
                />
            </section>

            <section
                id="project-section-annotation-ui"
                className={styles.settingsScrollAnchor}
                aria-label="Annotation UI"
            >
                <AnnotationUIPanel
                    annotationUI={annotationUI}
                    setAnnotationUI={setAnnotationUI}
                    availableUIs={availableUIs}
                    uisError={uisError}
                    settingsLocked={settingsLocked}
                    projectItems={projectItems}
                    folderId={folderId}
                    onReloadProjectItems={loadItems}
                />
            </section>

            <section
                id="project-section-items"
                className={styles.settingsScrollAnchor}
                aria-label="Items"
            >
                <ItemsPanel
                    folderId={folderId}
                    items={projectItems}
                    subfolders={projectSubfolders}
                    loading={itemsLoading}
                    error={itemsError}
                    onReload={loadItems}
                    onSetConfirmDelete={setConfirmDelete}
                    settingsLocked={settingsLocked}
                />
            </section>

            <RoiSettingsPanel
                supportsRoi={supportsProjectRoi}
                annotationUI={annotationUI}
                setAnnotationUI={setAnnotationUI}
                projectChrome={projectChrome}
                settingsLocked={settingsLocked}
            />

            <ItemMetadataSettingsPanel
                annotationUI={annotationUI}
                setAnnotationUI={setAnnotationUI}
                projectItems={projectItems}
                folderId={folderId}
                onReloadProjectItems={loadItems}
                settingsLocked={settingsLocked}
            />

            <NotesSettingsPanel
                notesConfig={notesConfig}
                setNotesConfig={setNotesConfig}
                settingsLocked={settingsLocked}
            />

            {confirmDelete && (
                <div className={styles.confirmOverlay}>
                    <div className={styles.confirmDialog}>
                        <p className={styles.confirmMsg}>
                            {confirmDelete.type === 'item'
                                ? <>Delete item <strong>{confirmDelete.name}</strong>? This cannot be undone.</>
                                : <>Delete subfolder <strong>{confirmDelete.name}</strong> and all its items? This cannot be undone.</>}
                        </p>
                        {confirmDeleteError && (
                            <p className={styles.itemActionError}>{confirmDeleteError}</p>
                        )}
                        <div className={styles.confirmActions}>
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                onClick={() => { setConfirmDelete(null); setConfirmDeleteError(null); }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.buttonDanger}
                                onClick={handleConfirmDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProjectEditForm;
